import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatCalendar } from '@angular/material/datepicker';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import {
    IDep,
    IFilial,
    IRecTalon,
    IRnumbDate,
    ITalonModal,
    ITalonModalResult,
} from 'src/app/interfaces/record.interfaces';
import { NewTalonComponentMobile } from 'src/app/modals/new-talon-mobile/talon.component';
import { RecordService } from 'src/app/services/record.service';
import { Utils } from 'src/app/services/utils';
import { AlertService } from 'src/lib/ariadna/alert';
import {
    formatUiDate,
    toDateTime,
    uniqueDateTimesByDay,
} from 'src/app/shared/date/date.utils';
import { strToDate } from '../application/global.function';
import { CalendarHeader } from './header/calendar-header.component';

@Component({
    selector: 'app-talonpicker',
    templateUrl: './talonpicker.component.html',
    styleUrls: ['./talonpicker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class TalonpickerComponent implements AfterViewInit, OnChanges {
    @Input() public params!: IRnumbDate;
    @Output() public talonPickerClosed = new EventEmitter<void>();
    @ViewChild('calendar') public calendar?: MatCalendar<DateTime>;
    @ViewChild('pickerContainer', { read: ElementRef })
    public pickerContainer?: ElementRef<HTMLElement>;

    public page: 'pickFilial' | 'pickDateTime' = 'pickFilial';
    public loading = true;
    public activeDays: DateTime[] = []; // NOT sorted by date
    public selectedDay: DateTime | null = DateTime.local();
    public readonly minDate: DateTime = DateTime.local();
    public maxDate: DateTime = DateTime.local().plus({ months: 1 });

    public talonListAll: IRecTalon[] = [];
    public talonListFiltered: IRecTalon[] = [];
    public depList: IDep[] = [];
    public selectedDep: number | null = null;
    public selectedDeps: number[] = [];
    public readonly header = CalendarHeader;
    public pickedTalon: IRecTalon | null = null;
    public filialFromParams: IFilial[] = [];
    public isClosing = false;

    public readonly dateFilter = (date: DateTime | null): boolean =>
        date !== null && this.isActiveDay(date);

    private isTalonFlowActive = false;

    public get selectedDayText(): string {
        return formatUiDate(this.selectedDay);
    }

    public constructor(
        private rs: RecordService,
        private alertService: AlertService,
        private modal: MatDialog,
        private cdref: ChangeDetectorRef,
        private renderer: Renderer2,
    ) {}

    public async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (!changes['params']) return;

        this.filialFromParams = this.params.Idep
            ? Array.isArray(this.params.Idep)
                ? this.params.Idep
                : [this.params.Idep]
            : [];
        this.loading = true;

        try {
            this.maxDate = toDateTime(this.params.periodEnd);
            await this.getTalonList(this.params);
            await this.getDepList(this.params);
            this.initializeDepartmentSelection();
            this.updateTalonView(true);
            this.pickedTalon = null;
        } catch (error: unknown) {
            const errorMessage = Utils.getErrorMessage(error);
            console.error(`Не удалось загрузить календарь: ${errorMessage}`);
            this.alertService.error('Ошибка получения номерков');
            setTimeout(() => this.closeCalendar(), 1000);
        } finally {
            this.loading = false;
        }
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            const pickerContainer = this.pickerContainer?.nativeElement;
            if (pickerContainer)
                this.renderer.addClass(pickerContainer, 'opened');
        }, 100);
    }

    private async getTalonList(params: IRnumbDate): Promise<void> {
        const talons = await lastValueFrom(this.rs.getRnumbListv2(params));
        this.talonListAll = talons.map((talon) => ({
            ...talon,
            dtBegin: strToDate(talon.datBeginStr),
        }));
    }

    private async getDepList(params: IRnumbDate): Promise<void> {
        const fullDepList = await lastValueFrom(this.rs.getDepListv2(params));
        const depsWithTalons = new Set(
            this.talonListAll.map((talon) => Number(talon.depId)),
        );
        const filteredByTalons = fullDepList.filter((dep) =>
            depsWithTalons.has(Number(dep.keyId)),
        );

        this.depList = this.mergeDepartmentsByName(filteredByTalons);
    }

    private mergeDepartmentsByName(deps: IDep[]): IDep[] {
        const map = new Map<string, IDep>();
        for (const dep of deps) {
            const existing = map.get(dep.text);
            if (existing) {
                const mergedKeyIds = existing.mergedKeyIds ?? [existing.keyId];
                mergedKeyIds.push(dep.keyId);
                existing.mergedKeyIds = mergedKeyIds;
            } else {
                map.set(dep.text, { ...dep, mergedKeyIds: [dep.keyId] });
            }
        }
        return Array.from(map.values());
    }

    public selectDay(day: DateTime | null): void {
        this.selectedDay = day;
        this.talonListFiltered = this.filterTalons();
    }

    public selectDep(dep: IDep): void {
        this.pickedTalon = null;
        this.selectedDep = dep.keyId;
        this.selectedDeps = dep.mergedKeyIds ?? [dep.keyId];
        this.page = 'pickDateTime';
        this.updateTalonView(true);
        this.cdref.detectChanges();
    }

    private initializeDepartmentSelection(): void {
        const selectedDepartment = this.depList.find(
            (dep) => dep.text === this.filialFromParams[0]?.depName,
        );
        this.selectedDep = selectedDepartment?.keyId ?? null;
        this.selectedDeps = selectedDepartment?.mergedKeyIds ?? [];
        this.page =
            this.depList.length === 1 || this.selectedDep !== null
                ? 'pickDateTime'
                : 'pickFilial';
    }

    private updateCalendar(): void {
        if (this.calendar) this.calendar.updateTodaysDate();
    }

    private isActiveDay(day: DateTime): boolean {
        return this.activeDays.some(
            (activeDay) => day.hasSame(activeDay, 'day'),
        );
    }

    private filterActiveDays(): DateTime[] {
        return uniqueDateTimesByDay(
            this.talonListAll
                .filter((talon) => this.isTalonInSelectedDepartment(talon))
                .map((talon) => toDateTime(talon.dtBegin)),
        );
    }

    private findFirstActiveDay(): DateTime | null {
        if (this.activeDays.length === 0) return null;

        let firstActiveDay = this.activeDays[0];
        for (const day of this.activeDays) {
            if (firstActiveDay.toMillis() > day.toMillis()) firstActiveDay = day;
        }
        return firstActiveDay;
    }

    private filterTalons(): IRecTalon[] {
        const selectedDay = this.selectedDay;
        if (!selectedDay) return [];

        return this.talonListAll.filter(
            (talon) =>
                toDateTime(talon.dtBegin).hasSame(selectedDay, 'day') &&
                this.isTalonInSelectedDepartment(talon),
        );
    }

    private isTalonInSelectedDepartment(talon: IRecTalon): boolean {
        if (this.selectedDep === null) return true;
        if (this.selectedDeps.length === 0)
            return talon.depId === this.selectedDep;
        return this.selectedDeps.includes(talon.depId);
    }

    private updateTalonView(selectFirstActiveDay: boolean): void {
        this.activeDays = this.filterActiveDays();
        if (
            selectFirstActiveDay ||
            !this.selectedDay ||
            !this.isActiveDay(this.selectedDay)
        ) {
            this.selectedDay = this.findFirstActiveDay();
        }
        this.talonListFiltered = this.filterTalons();
        this.updateCalendar();
    }

    public async openTalonModal(
        event: MouseEvent,
        talon: IRecTalon | null,
    ): Promise<void> {
        event.stopPropagation();
        if (this.isTalonFlowActive || !talon) return;
        if (this.params.srv?.isTelemed !== talon.isTelemed) return;

        if (talon.rnumbId !== this.pickedTalon?.rnumbId) {
            this.pickedTalon = talon;
        }

        this.isTalonFlowActive = true;
        let dialogOpened = false;

        try {
            const rnumbId = await this.getBlockedRnumbId(talon);
            const data: ITalonModal = {
                rnumbId,
                info: {
                    docdepID: talon.docDepId,
                    beginDate: talon.dtBegin,
                },
                srv: this.params.srv,
                paramCansel: false,
                infoDoc: this.params.infoDoc,
            };

            if (rnumbId !== null)
                this.alertService.success(
                    `Талон успешно зарезервирован, № талона: ${rnumbId}`,
                );
            const dialogConfig = this.createDialogConfig(data);
            const modalRef = this.modal.open<
                NewTalonComponentMobile,
                ITalonModal,
                ITalonModalResult
            >(NewTalonComponentMobile, dialogConfig);
            modalRef.afterClosed().subscribe((result) => {
                this.isTalonFlowActive = false;
                if (result?.unlockTalon && result.rnumbID != null)
                    void this.unlockTalon(result.rnumbID);
            });
            dialogOpened = true;
        } catch (error: unknown) {
            const errorMessage = Utils.getErrorMessage(error);
            console.error(`Не удалось зарезервировать талон: ${errorMessage}`);
            this.alertService.error(
                `Не удалось записаться на номерок: ${errorMessage}`,
            );
            await this.refreshTalonsAfterReservationError();
        } finally {
            this.loading = false;
            if (!dialogOpened) this.isTalonFlowActive = false;
        }
    }

    private async getBlockedRnumbId(
        talon: IRecTalon,
    ): Promise<number | null> {
        let rnumbId: number | null = talon.rnumbId;
        if (
            talon.isInterval &&
            talon.isInterval > 0 &&
            talon.intervalId !== null
        ) {
            const result = await lastValueFrom(
                this.rs.getRnumbCreateInterval(
                    talon.intervalId,
                    talon.datBeginStr,
                    talon.datEndStr,
                ),
            );
            if (!result.guid)
                throw new Error('Ошибка создания интервального номерка');
            rnumbId = result.guid;
        }

        if (rnumbId !== null)
            await lastValueFrom(this.rs.setRnumbBlStatus(rnumbId));
        return rnumbId;
    }

    private createDialogConfig(
        data: ITalonModal,
    ): MatDialogConfig<ITalonModal> {
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        return {
            width: isMobile ? '100vw' : 'auto',
            height: 'auto',
            maxWidth: isMobile ? '100vw' : 'auto',
            minWidth: isMobile ? '100vw' : 'auto',
            closeOnNavigation: true,
            data,
        };
    }

    private async unlockTalon(rnumbId: number): Promise<void> {
        try {
            await lastValueFrom(this.rs.getRnumbUnlock(rnumbId));
            this.alertService.success(
                `Талон успешно разблокирован, № талона: ${rnumbId}`,
            );
        } catch (error: unknown) {
            this.alertService.error(
                `Не удалось разблокировать талон с id ${rnumbId}: ${Utils.getErrorMessage(error)}`,
            );
        }
    }

    private async refreshTalonsAfterReservationError(): Promise<void> {
        try {
            await this.getTalonList(this.params);
            this.pickedTalon = null;
            this.updateTalonView(false);
            this.cdref.detectChanges();
        } catch (error: unknown) {
            console.error(
                `Не удалось обновить список номерков: ${Utils.getErrorMessage(error)}`,
            );
        }
    }

    public closeCalendar(): void {
        this.isClosing = true;

        setTimeout(() => {
            this.talonPickerClosed.emit();
        }, 300);
    }

    public talonDisable(talon: IRecTalon): boolean {
        if (!this.params.srv) return false;
        return this.params.srv.isTelemed === talon.isTelemed;
    }

    public backToFil(): void {
        this.selectedDep = null;
        this.selectedDeps = [];
        this.updateTalonView(true);
        this.page = 'pickFilial';
    }
}
